import { format } from "date-fns";
import { PartnerProviderLog } from "@/types/partner-provider-log";
import { formatDurationMs, stringifyLogDetails } from "./partnerProviderLogs";

export interface PartnerProviderLogsExportRow {
  Time: string;
  Provider: string;
  Event: string;
  Operation: string;
  Status: string;
  Level: string;
  Message: string;
  Duration: string;
  "HTTP Status": string;
  "Hotel ID": string;
  "Tenant ID": string;
  "Error Code": string;
  Details: string;
}

interface MapPartnerProviderLogsForExportOptions {
  getEventLabel: (value: string) => string;
  getStatusLabel: (value: string) => string;
  getLevelLabel: (value: string) => string;
}

const escapeCell = (value: unknown) => {
  const stringValue = String(value ?? "");

  return stringValue
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const escapeCsvField = (value: unknown) => {
  const stringValue = String(value ?? "");

  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

export const buildPartnerProviderLogsExportFilename = (
  extension: "csv" | "xls",
) => {
  const timestamp = format(new Date(), "yyyy-MM-dd-HH-mm");

  return `partner-provider-logs-${timestamp}.${extension}`;
};

export const mapPartnerProviderLogsForExport = (
  logs: PartnerProviderLog[],
  options: MapPartnerProviderLogsForExportOptions,
): PartnerProviderLogsExportRow[] =>
  logs.map((log) => ({
    Time: format(new Date(log.createdAt), "yyyy-MM-dd HH:mm:ss"),
    Provider: String(log.provider ?? ""),
    Event: options.getEventLabel(String(log.eventType ?? "")),
    Operation: log.operation || "-",
    Status: options.getStatusLabel(String(log.status ?? "")),
    Level: options.getLevelLabel(String(log.level ?? "")),
    Message: log.message || "-",
    Duration: formatDurationMs(log.durationMs),
    "HTTP Status": log.httpStatus !== null ? String(log.httpStatus) : "-",
    "Hotel ID": log.hotelId || "-",
    "Tenant ID": log.tenantId || "-",
    "Error Code": log.errorCode || "-",
    Details: stringifyLogDetails(log.details),
  }));

export const downloadPartnerProviderLogsCsv = (
  rows: PartnerProviderLogsExportRow[],
  filename: string,
) => {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => escapeCsvField(row[header as keyof typeof row]))
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadPartnerProviderLogsExcel = (
  rows: PartnerProviderLogsExportRow[],
  filename: string,
) => {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const tableHeader = headers
    .map((header) => `<th>${escapeCell(header)}</th>`)
    .join("");
  const tableRows = rows
    .map(
      (row) =>
        `<tr>${headers
          .map(
            (header) =>
              `<td>${escapeCell(row[header as keyof typeof row])}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8" />
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #d4d4d8; padding: 8px; text-align: left; vertical-align: top; }
          th { background: #f4f4f5; font-weight: 700; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>${tableHeader}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `.trim();

  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
